import React, { useState } from 'react';
import {
  faEdit,
  faSave,
  faTimesCircle,
  faUserEdit
} from '@fortawesome/pro-solid-svg-icons';
import { FormInstance } from '@lightbeamhealth/blueprint-form';
import { Classes } from '@blueprintjs/core';
import { Row, Col } from '../../../grid';
import Icon from '../../atoms/icon';
import Tag from '../../atoms/tag';
import Button from '../../molecules/button';
import Drawer from '../../molecules/drawer';
import InfoField from '../../molecules/info-field';
import Toast from '../../organisms/toast';
import Card from '../../organisms/card';
import Modal from '../../organisms/modal';
import PatientForm, { GetPatientData } from '../../organisms/patient/form';
import { FormatPhone } from '../../../libs/format';
import {
  SavePatient,
  PatientFormData,
  GetPatientPhone,
  GetCaregiverPhone
} from '../../../libs/patient';
import { useTriageUpdater } from '../../../contexts/triage/updater';
import { useTriagePatient } from '../../../contexts/triage/patient';
import { useMetaData } from '../../../contexts/metadata';

const PatientInfo = (): JSX.Element => {
  const { terms } = useMetaData();
  const { isReadOnly, account } = useMetaData();
  const {
    patientData: { patient }
  } = useTriagePatient();
  const { handleUpdatePatient } = useTriageUpdater();
  const form = new FormInstance<PatientFormData>(GetPatientData(patient));
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const getTags = (): React.ReactNodeArray => {
    const tags: React.ReactNodeArray = [];
    const parts = patient.tags ? patient.tags.split(',') : [];
    for (const [i, part] of parts.entries()) {
      tags.push(<Tag key={i} text={part} />);
    }
    return tags;
  };
  const save = (data: PatientFormData): void => {
    if (isSaving) {
      return;
    }
    setIsSaving(true);
    SavePatient(data)
      .then((patient) => {
        setIsSaving(false);
        handleUpdatePatient(patient);
        setIsFormOpen(false);
        Toast({
          message: `${terms.patient.capitalized} Updated!`,
          intent: 'success'
        });
      })
      .catch((error) => {
        setIsSaving(false);
        Modal.error({
          title: 'Error Saving Details',
          content: error.message
        });
      });
  };
  // Handle patient phones
  const patientPhone = GetPatientPhone(patient.phone, patient.phoneIsCaregiver);
  const caregiverPhone = GetCaregiverPhone(
    patient.phone,
    patient.phoneIsCaregiver,
    patient.caregiverPhone
  );
  return (
    <div>
      <h3>{terms.patient.capitalized} Information</h3>
      <Card className="mb-6">
        <Row>
          <Col sm={8}>
            <InfoField title="First Name" value={patient.firstName} />
          </Col>
          <Col sm={4}>
            <InfoField title="M. I." value={patient.middleInitial} />
          </Col>
          <Col sm={12}>
            <InfoField title="Last Name" value={patient.lastName} />
          </Col>
        </Row>
        <Row>
          <Col sm={12}>
            <InfoField title="Phone" value={FormatPhone(patientPhone)} />
          </Col>
          <Col sm={12}>
            <InfoField
              title="Caregiver Phone"
              value={FormatPhone(caregiverPhone)}
            />
          </Col>
        </Row>
        <Row>
          <Col sm={12}>
            <InfoField title="Date of Birth" value={patient.dob} />
          </Col>
          <Col sm={12}>
            <InfoField title={terms.mrn.capitalized} value={patient.mrn} />
          </Col>
        </Row>
        <Row>
          <Col sm={12}>
            <InfoField title="Gender" value={patient.gender} />
          </Col>
          {account?.isTimeTrackedEnabled ? (
            <Col sm={12}>
              <InfoField
                title="Consented to CCM"
                value={patient.ccmEligible ? 'Yes' : 'No'}
              />
            </Col>
          ) : null}
        </Row>
        <Row>
          <Col xs={24}>
            <h4>Description / Note</h4>
            {patient.note ? (
              <div
                className="mb-4"
                dangerouslySetInnerHTML={{ __html: patient.note }}
              />
            ) : (
              <p>
                <span className="bp5-text-muted">NONE</span>
              </p>
            )}
          </Col>
        </Row>
        <Row>
          <Col xs={24}>
            <InfoField title="Tags" value={getTags()} />
          </Col>
        </Row>
      </Card>
      {!isReadOnly && (
        <Button
          className="mb-6"
          intent="primary"
          icon={<Icon className="text-white" icon={faEdit} />}
          onClick={() => setIsFormOpen(true)}
        >
          Edit {terms.patient.capitalized} Info
        </Button>
      )}
      <Drawer
        title={
          <span>
            <Icon className="mr-4" icon={faUserEdit} />
            {`${terms.patient.capitalized} Details`}
          </span>
        }
        lazy={false}
        canEscapeKeyClose={false}
        canOutsideClickClose={false}
        isCloseButtonShown={false}
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onOpened={() => form.setData(GetPatientData(patient))}
      >
        <div className={Classes.DRAWER_BODY}>
          <PatientForm form={form} onSubmit={save} />
          <div className={Classes.DRAWER_FOOTER}>
            <Button
              className="mr-4"
              icon={<Icon title={false} className="text-white" icon={faSave} />}
              intent="primary"
              loading={isSaving}
              onClick={() => form.submit()}
            >
              Save Details
            </Button>
            <Button
              icon={<Icon title={false} icon={faTimesCircle} />}
              onClick={() => setIsFormOpen(false)}
              disabled={isSaving}
            >
              Cancel Changes
            </Button>
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default PatientInfo;
